// Shared look for all dashboards (Protocol, Sarees, Annaprasadam, Hundial): a red hero banner with the
// temple's god image in the middle and glass stat tiles on both sides, then orange-headed section cards
// with light coloured tiles. The god image is uploaded per temple and per dashboard by the Super Admin.
import React, { useEffect, useState } from "react";
import { Landmark } from "lucide-react";
import api from "../lib/api";
import { useIstToday, istLongDate } from "../lib/istDate";

let templesPromise = null;
const loadTemples = () => {
  if (!templesPromise) templesPromise = api.get("/meta/form-data").then((r) => r.data.temples || []).catch(() => { templesPromise = null; return []; });
  return templesPromise;
};
export const refreshDashTemples = () => { templesPromise = null; };

export const dashImageUrl = (tid, kind, v) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/public/dash-image/${tid}/${kind}?v=${v}`;

/** The god image for this dashboard: the chosen temple's, else the user's temple, else (Super Admin) any temple that has one. */
export function useDashImage(kind, templeId, user) {
  const [temples, setTemples] = useState([]);
  useEffect(() => { loadTemples().then(setTemples); }, []);
  const has = (t) => t && (t.dash_image_v || {})[kind];
  const pick = temples.find((t) => t.temple_id === (templeId || user?.temple_id));
  const t = has(pick) ? pick : (!templeId && user?.role === "SUPER_ADMIN" ? temples.find(has) : null);
  return { url: t ? dashImageUrl(t.temple_id, kind, t.dash_image_v[kind]) : "", temple: pick || null, temples };
}

// Smaller text for long values, so numbers are never cut off.
const fit = (v, big, mid, small) => { const l = String(v ?? "").length; return l > 9 ? small : l > 6 ? mid : big; };



function HeroTile({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-2xl bg-white/15 border border-white/30 backdrop-blur-sm px-3.5 py-3.5 lg:py-4 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-full bg-white/20 border border-white/30 flex items-center justify-center shrink-0"><Icon className="w-[18px] h-[18px]" /></span>
        <div className="min-w-0">
          <p className="text-[12px] sm:text-[13px] font-semibold leading-tight line-clamp-2 opacity-95">{label}</p>
          <p className={`${fit(value, "text-xl sm:text-2xl", "text-lg sm:text-xl", "text-base sm:text-lg")} font-extrabold leading-tight tracking-tight whitespace-nowrap`} data-testid={testid && `${testid}-value`}>
            {value}{sub != null && sub !== "" && <span className="text-sm font-semibold opacity-80"> ({sub})</span>}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Hero banner: up to 4 tiles each side, god image in the middle. */
export function DashHero({ image, left = [], right = [], alt = "Temple deity", testid }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [image]);
  const showImg = image && !broken;
  return (
    <div className="relative pt-10 sm:pt-14 lg:pt-16" data-testid={testid}>
      <div className="relative rounded-2xl bg-gradient-to-r from-[#A50F1F] via-[#CF2A1E] to-[#E7561C] shadow-lg">
        <div className="absolute inset-0 rounded-2xl bg-[radial-gradient(ellipse_at_center,rgba(255,214,102,0.35),transparent_60%)] pointer-events-none" />
        <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_minmax(240px,320px)_1fr] gap-4 p-4 sm:p-5 items-center">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 order-2 lg:order-1">{left.map((t, i) => <HeroTile key={i} {...t} />)}</div>
          {/* God image: rises above the banner and slightly overlaps its bottom edge, with a golden glow behind */}
          <div className="order-1 lg:order-2 relative flex items-end justify-center -mt-12 sm:-mt-16 lg:-mt-[5.5rem] lg:-mb-8 lg:self-stretch">
            <div className="absolute left-1/2 top-[55%] -translate-x-1/2 -translate-y-1/2 w-[120%] max-w-[380px] aspect-square rounded-full bg-[radial-gradient(circle,rgba(255,251,230,0.95)_0%,rgba(255,220,130,0.6)_32%,rgba(255,170,60,0)_68%)] blur-md pointer-events-none" />
            {showImg ? (
              <img src={image} alt={alt} onError={() => setBroken(true)} data-testid="dash-god-image"
                className="relative h-56 sm:h-64 lg:h-[300px] w-auto max-w-full object-contain drop-shadow-[0_12px_20px_rgba(0,0,0,0.5)]" />
            ) : (
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gradient-to-b from-[#FFE7A3] to-[#D4A017] border-4 border-white/60 shadow-xl flex items-center justify-center" data-testid="dash-god-placeholder">
                <Landmark className="w-14 h-14 text-[#7A0F2E]" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 order-3">{right.map((t, i) => <HeroTile key={i} {...t} />)}</div>
        </div>
      </div>
    </div>
  );
}

/** Line under the hero: temple names in the middle, date (or any controls) on the right. */
export function DashCaption({ temple, title, children }) {
  const day = useIstToday(); // re-render at 00:00 IST
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1">
      <div className="flex-1 min-w-[220px] text-center">
        {temple?.name_telugu && <span className="font-telugu text-[#6B1FA0] text-base sm:text-lg mr-2">{temple.name_telugu}</span>}
        <span className="font-heading font-bold text-[#B0121F] text-lg sm:text-xl">{title}</span>
        {temple?.name && <span className="block text-xs text-slate-500">{temple.name}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {children}
        <div className="rounded-lg border border-[#E5D9C5] bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm">
          <span className="block text-[10px] uppercase tracking-wider text-slate-400 leading-none mb-0.5">Today · 00:00 – 23:59 IST</span><span data-testid="dash-today" data-day={day}>{istLongDate()}</span>
        </div>
      </div>
    </div>
  );
}

const TONES = {
  blue: ["bg-[#EEF4FF]", "bg-[#DCE7FF]", "text-[#3566D6]"],
  green: ["bg-[#EAF8EF]", "bg-[#D2F1DC]", "text-[#1F9D55]"],
  red: ["bg-[#FDEEEF]", "bg-[#FAD6D9]", "text-[#DC2F3B]"],
  yellow: ["bg-[#FFF8E1]", "bg-[#FCE9AE]", "text-[#C99A06]"],
  purple: ["bg-[#F4EEFF]", "bg-[#E6DAFF]", "text-[#7C3AED]"],
  orange: ["bg-[#FFF2E8]", "bg-[#FFDCC2]", "text-[#E2620F]"],
  slate: ["bg-[#F3F4F6]", "bg-[#E2E5EA]", "text-[#475569]"],
};

/** Light coloured tile inside a section card. */
export function Tile({ icon: Icon, label, value, note, tone = "blue", testid }) {
  const [bg, ring, fg] = TONES[tone] || TONES.blue;
  return (
    <div className={`rounded-xl ${bg} px-2 py-3 text-center min-w-0`} data-testid={testid}>
      <span className={`mx-auto w-11 h-11 rounded-full ${ring} flex items-center justify-center`}><Icon className={`w-5 h-5 ${fg}`} /></span>
      <p className="mt-2 text-[12px] font-semibold text-slate-700 leading-tight line-clamp-2">{label}</p>
      <p className={`${fit(value, "text-xl sm:text-2xl", "text-lg sm:text-xl", "text-sm sm:text-base")} font-extrabold ${fg} leading-tight whitespace-nowrap`} data-testid={testid && `${testid}-value`}>{value}</p>
      {note && <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{note}</p>}
    </div>
  );
}

/** Section card with the orange-to-red title bar. */
export function Section({ title, right, children, className = "", bodyClass = "p-3", testid }) {
  return (
    <div className={`rounded-xl border border-[#F2DCCB] bg-white shadow-sm overflow-hidden ${className}`} data-testid={testid}>
      <div className="relative bg-gradient-to-r from-[#F38B1C] via-[#E8551E] to-[#C3122A] text-white text-center font-semibold text-sm py-2 px-10">
        {title}
        {right && <div className="absolute right-2 top-1/2 -translate-y-1/2">{right}</div>}
      </div>
      <div className={bodyClass}>{children}</div>
    </div>
  );
}

export const TileGrid = ({ cols = 3, children }) => (
  <div className={`grid gap-2 ${cols === 1 ? "grid-cols-1 w-1/2 mx-auto" : cols === 2 ? "grid-cols-2" : cols === 4 ? "grid-cols-2 sm:grid-cols-4" : cols === 5 ? "grid-cols-3 sm:grid-cols-5" : "grid-cols-3"}`}>{children}</div>
);

/** Table styled like the reference "Holding Area" card. */
export function DashTable({ head, rows, foot, empty = "No data yet", testid, align }) {
  const al = (i) => (align?.[i] || (i === 0 ? "left" : "right"));
  return (
    <div className="rounded-lg border border-[#EFE6DA] overflow-hidden">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="bg-[#F7F3EE] text-[11px] font-bold text-slate-600">{head.map((h, i) => <th key={i} className={`px-2.5 py-2 text-${al(i)}`}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={head.length} className="py-6 text-center text-slate-400">{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[#F1EBE3]">{r.map((c, j) => <td key={j} className={`px-2.5 py-2 text-${al(j)} ${j === 0 ? "text-slate-700" : "font-bold"}`}>{c}</td>)}</tr>
          ))}
        </tbody>
        {foot && rows.length > 0 && <tfoot><tr className="border-t-2 border-[#EADFD2] bg-[#FBF8F4] font-extrabold text-slate-800">{foot.map((c, i) => <td key={i} className={`px-2.5 py-2 text-${al(i)}`}>{c}</td>)}</tr></tfoot>}
      </table>
    </div>
  );
}

export const n = (v) => Number(v || 0).toLocaleString("en-IN");
export const rupees = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
